import { useAtomValue } from "jotai";
import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { checkHasLiveTag } from "scmp-app/components/article/article-render/helpers";
import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { checkHasSeriesShortLabel } from "scmp-app/components/content/content-series-short-label/helpers";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import { usePgType, usePlusQueryParameters } from "scmp-app/components/content/helpers";
import { PlusFlag } from "scmp-app/components/plus/plus-flag";
import { shouldShowMostPopularTagAtom } from "scmp-app/pages/home/atoms";
import type { homeSecondaryContentItemContent$key } from "scmp-app/queries/__generated__/homeSecondaryContentItemContent.graphql";

import {
  ActionBar,
  Container,
  CoverImage,
  Header,
  Headline,
  LiveTagContainer,
  MostPopularTagContainer,
  SectionContainer,
  SponsorTagContainer,
  StyledCoverEntityLink,
  StyledHeadlineEntityLink,
  SubHeadline,
  Summary,
  TopicLabel,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  currentTopicReference?: string;
  hideHeadlineTag?: boolean;
  isReadingTimeEnabled?: boolean;
  reference: homeSecondaryContentItemContent$key;
  skipHighlight?: boolean;
  withBookmark?: boolean;
  withComment?: boolean;
  withImage?: boolean;
  withLiveTag?: boolean;
  withMostPopularTag?: boolean;
  withSection?: boolean;
  withSeriesShortLabel?: boolean;
  withSubHeadline?: boolean;
  withSummary?: boolean;
  withTopic?: boolean;
  withVideoPlayIcon?: boolean;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  currentTopicReference,
  hideHeadlineTag = false,
  isReadingTimeEnabled = false,
  onClick,
  ref: forwardReference,
  reference: reference_,
  skipHighlight = false,
  topicLinkVariant,
  withBookmark = false,
  withComment = false,
  withImage = false,
  withLiveTag = false,
  withMostPopularTag = false,
  withSection = false,
  withSeriesShortLabel = false,
  withSubHeadline = false,
  withSummary = false,
  withTopic = false,
  withVideoPlayIcon = false,
}) => {
  const content = useFragment(
    graphql`
      fragment homeSecondaryContentItemContent on Content {
        entityId
        urlAlias
        ...entityLink
        ...helpersCheckIsPlusArticle
        ...helpersCheckHasLiveTagArticle
        ...helpersCheckHasSeriesShortLabelContent
        ...helpersUseContentSponsorContent
        ...hooksContentItemProvidersContent
          @arguments(
            withSections: true
            withHeadline: true
            withLiveTag: true
            withSummary: true
            withSubHeadline: true
            withActionBar: true
            withCoverImage: true
            withCoverImageSize1200x800: true
            withCoverImageSize540x360: true
            withSponsorTag: true
            withVideoDurationOverlay: true
            withTopicLink: true
            withSeriesShortLabel: true
            withMostPopularTag: true
          )
      }
    `,
    reference_,
  );

  const shouldShowMostPopularTag = useAtomValue(shouldShowMostPopularTagAtom);
  const providers = useContentItemProviders(content, topicLinkVariant);
  const hasLiveTag = checkHasLiveTag(content);
  const isShowLiveTag = hasLiveTag && withLiveTag;
  const isPlusArticle = checkIsPlusArticle(content);
  const hasSeriesShortLabel = checkHasSeriesShortLabel(content);

  const contentSponsor = getContentSponsor(content);
  const isSponsorArticle = notEmpty(contentSponsor?.name);

  const pgtype = usePgType();
  const plusQueryParameters = usePlusQueryParameters(content);

  const renderHeader = () => {
    switch (true) {
      case isShowLiveTag:
        return <LiveTagContainer>{providers.liveTag()}</LiveTagContainer>;
      case withTopic:
        return (
          <TopicLabel>
            {providers.topicLink?.({
              currentTopicReference,
              preferShortName: true,
            })}
          </TopicLabel>
        );
      case withSection:
        return (
          <SectionContainer>
            {withSeriesShortLabel && hasSeriesShortLabel
              ? providers.seriesShortLabel?.()
              : providers.sections()}
          </SectionContainer>
        );
      default:
    }
  };

  const renderMostPopularTag = () => {
    const header = renderHeader();
    if (withMostPopularTag && shouldShowMostPopularTag) {
      return (
        <MostPopularTagContainer>
          {providers.mostPopularTag({ fallback: header })}
        </MostPopularTagContainer>
      );
    }
    return header;
  };

  return (
    <Container className={className} ref={forwardReference}>
      <Header>
        {isPlusArticle && <PlusFlag pgtype={pgtype} />} {renderMostPopularTag()}
      </Header>
      <StyledHeadlineEntityLink
        onClick={() => onClick?.(content.entityId, content.urlAlias)}
        query={plusQueryParameters}
        reference={content}
      >
        <Headline>
          {providers.headline({
            hideTag: hideHeadlineTag,
            hideTooltip: true,
            preferSocialHeadline: true,
            skipHighlight,
          })}
        </Headline>
        {withSummary && (
          <Summary>
            {providers.summary({
              isTextOverflowEllipsis: true,
            })}
          </Summary>
        )}
        {withSubHeadline && !isSponsorArticle && (
          <SubHeadline>{providers.subHeadline()}</SubHeadline>
        )}
      </StyledHeadlineEntityLink>
      {isSponsorArticle ? (
        <SponsorTagContainer>{providers.sponsorTag?.({ variant: "home" })}</SponsorTagContainer>
      ) : (
        <ActionBar>
          {providers.actionBar({
            commentVariant: "reverse",
            displayDateVariant: isReadingTimeEnabled ? "readingTime" : "updatedDate",
            isBookmarkVisible: withBookmark ?? false,
            isCommentsVisible: withComment ?? false,
          })}
        </ActionBar>
      )}
      {withImage && (
        <StyledCoverEntityLink
          $isSponsorArticle={isSponsorArticle}
          onClick={() => onClick?.(content.entityId, content.urlAlias)}
          query={plusQueryParameters}
          reference={content}
        >
          <CoverImage>
            {providers.coverImage({
              responsiveVariants: {
                desktopUp: "size1200x800",
                mobileUp: "size540x360",
                tabletUp: "size1200x800",
              },
            })}
            {withVideoPlayIcon && providers.video?.videoPlayIcon()}
            {providers.video?.durationOverlay({
              iconSize: "hidden",
              variant: "homepage",
            })}
          </CoverImage>
        </StyledCoverEntityLink>
      )}
    </Container>
  );
};

Component.displayName = "ContentItemHomeSecondary";

export const ContentItemHomeSecondary = withHighlightedHeadline(Component);
